/**
 * App-shell redirect helpers for the "Classic / New" app version toggle.
 *
 * Two configured environments live behind separate origins:
 *   - REACT_APP_APP_BASE_URL      → legacy ("Classic") shell
 *   - REACT_APP_NEW_APP_BASE_URL  → rewritten ("New") shell
 *
 * The user's preference is stored on their profile as `isNewApp`. We use it
 * in two places:
 *
 *   1. `AppHeader` toggle  → user explicitly clicks Classic/New. After we
 *      persist the new value we want to navigate, even from `localhost` or
 *      any other host, because the user clearly asked for it. Use
 *      `redirectToAppShell` directly.
 *
 *   2. `AppSidebar` bootstrap → on every authenticated mount we refetch the
 *      profile. If the user's `isNewApp` doesn't match the shell they happen
 *      to be on (e.g. they bookmarked the old URL but flipped the toggle on
 *      a different device), redirect them silently. Use
 *      `maybeRedirectToMatchingAppShell`, which is intentionally stricter:
 *      it only redirects when the current origin matches the *other*
 *      configured shell, so dev environments (where neither URL matches
 *      `localhost`) and misconfigured setups are no-ops.
 */

const stripTrailingSlash = (u?: string) =>
  u ? u.replace(/\/+$/, "") : "";

export const OLD_APP_URL = stripTrailingSlash(process.env.REACT_APP_APP_BASE_URL);
export const NEW_APP_URL = stripTrailingSlash(process.env.REACT_APP_NEW_APP_BASE_URL);

/** Returns the configured base URL for the requested shell, or `""` if the env var is missing. */
export function getAppShellBaseUrl(isNewApp: boolean): string {
  return isNewApp ? NEW_APP_URL : OLD_APP_URL;
}

/** Safely parses an origin from a base URL; returns `null` on bad input. */
function tryGetOrigin(base: string): string | null {
  if (!base) return null;
  try {
    return new URL(base).origin;
  } catch (_) {
    return null;
  }
}

/**
 * Hard-redirects to `targetBase` preserving the current path/search/hash.
 * No-ops when the target's origin already matches the current page (so
 * callers can call this unconditionally without forcing a full reload).
 */
export function redirectToAppShell(targetBase: string): boolean {
  if (!targetBase) return false;
  const targetOrigin = tryGetOrigin(targetBase);
  if (!targetOrigin) return false;
  if (targetOrigin === window.location.origin) return false;

  const path = window.location.pathname || "/";
  const search = window.location.search || "";
  const hash = window.location.hash || "";
  window.location.href = `${targetBase}${path}${search}${hash}`;
  return true;
}

/**
 * Bootstrap redirect: when the user's `isNewApp` preference disagrees with
 * the shell they're currently on, send them to the matching shell. Strict
 * mode — we only redirect when:
 *   - both shell URLs are configured,
 *   - the current origin matches the *other* shell exactly.
 *
 * Returning `false` is the "do nothing" path; that covers dev (`localhost`),
 * misconfigured envs, or the user already being on the right shell.
 */
export function maybeRedirectToMatchingAppShell(isNewApp: boolean): boolean {
  const targetBase = getAppShellBaseUrl(isNewApp);
  const otherBase = getAppShellBaseUrl(!isNewApp);
  if (!targetBase || !otherBase) return false;

  const targetOrigin = tryGetOrigin(targetBase);
  const otherOrigin = tryGetOrigin(otherBase);
  if (!targetOrigin || !otherOrigin) return false;

  if (window.location.origin === targetOrigin) return false;
  if (window.location.origin !== otherOrigin) return false;

  return redirectToAppShell(targetBase);
}
